import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchTabUsersList,
  setDestroy,
} from "containers/signontab-users/signontab-users-container.slice";
import { patchTabUser, postTabUser } from "services/signontab.service";
import {
  CatalogTable,
  ChangeLink,
  EditPanel,
  SettingsCard,
  useSaveWithToast,
  useScrollIntoViewOnOpen,
} from "containers/settings/ui";
import {
  AddButton,
  Field,
  FIELD_INPUT_CLASS,
  PanelHeading,
} from "containers/settings/catalog-form-parts";

// The password is never loaded: the list does not carry it, so it starts empty.
// The server needs it on every save, new user or not, so the form asks for it each time.
type TabUserDraft = {
  id?: string;
  name: string;
  username: string;
  password: string;
};

const NEW_TAB_USER: TabUserDraft = { name: "", username: "", password: "" };

type TabUserEditPanelProps = {
  saved: TabUserDraft;
  companyId: string;
  onSaved: () => void;
  onCancel: () => void;
};

function TabUserEditPanel({
  saved,
  companyId,
  onSaved,
  onCancel,
}: TabUserEditPanelProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [draft, setDraft] = useState(saved);
  const [triedToSave, setTriedToSave] = useState(false);

  const name = draft.name.trim();
  const username = draft.username.trim();
  const isIncomplete = !name || !username || draft.password === "";

  const submit = async () => {
    setTriedToSave(true);
    if (isIncomplete) return;

    const payload = {
      id: draft.id,
      name,
      username,
      password: draft.password,
    };
    const wasSaved = await save(() =>
      draft.id
        ? patchTabUser(payload, companyId)
        : postTabUser(payload, companyId)
    );
    if (wasSaved) onSaved();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onCancel}
      saving={saving}
      error={triedToSave && isIncomplete ? t("requiredFields") : error}
    >
      <PanelHeading>
        {saved.id
          ? `${t("settings_change")}: ${saved.name}`
          : t("signontab_users_add")}
      </PanelHeading>
      <div className="space-y-3">
        <Field htmlFor="tab-user-name" label={t("signontab_users_name")}>
          <input
            id="tab-user-name"
            className={FIELD_INPUT_CLASS}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            autoComplete="off"
            autoFocus
          />
        </Field>
        <Field
          htmlFor="tab-user-username"
          label={t("signontab_users_username")}
        >
          <input
            id="tab-user-username"
            className={FIELD_INPUT_CLASS}
            value={draft.username}
            onChange={(e) => setDraft({ ...draft, username: e.target.value })}
            autoComplete="off"
          />
        </Field>
        <Field
          htmlFor="tab-user-password"
          label={t("signontab_users_password")}
          hint={saved.id ? t("signontab_users_password_edit_hint") : undefined}
        >
          <input
            id="tab-user-password"
            type="password"
            className={FIELD_INPUT_CLASS}
            value={draft.password}
            onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            autoComplete="new-password"
          />
        </Field>
      </div>
    </EditPanel>
  );
}

function SignontabUsersContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { collection, isLoading, isError } = useAppSelector(
    (state) => state.signontabUserContainerReducer
  );
  const [editing, setEditing] = useState<TabUserDraft | null>(null);
  const panelRef = useScrollIntoViewOnOpen(editing);

  useEffect(() => {
    if (companyId) dispatch(fetchTabUsersList(companyId));
    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, dispatch]);

  if (!companyId) return null;

  const startEdit = (id: string) => {
    const row = collection.find((tabUser) => tabUser.id === id);
    if (row) {
      setEditing({
        id: row.id,
        name: row.name,
        username: row.username,
        password: "",
      });
    }
  };

  const closeAndReload = () => {
    setEditing(null);
    dispatch(fetchTabUsersList(companyId));
  };

  return (
    <SettingsCard
      title={t("settings_signing_tablets_link")}
      description={t("signontab_users_card_description")}
      actions={
        <AddButton
          label={t("signontab_users_add")}
          onClick={() => setEditing(NEW_TAB_USER)}
        />
      }
    >
      {editing && (
        <div ref={panelRef} className="mb-4">
          <TabUserEditPanel
            key={editing.id ?? "new"}
            saved={editing}
            companyId={companyId}
            onSaved={closeAndReload}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
      <CatalogTable
        columns={[
          { key: "name", header: t("signontab_users_name") },
          { key: "username", header: t("signontab_users_username") },
        ]}
        rows={collection.map((tabUser) => ({
          id: tabUser.id,
          cells: {
            name: <span className="font-medium">{tabUser.name}</span>,
            username: tabUser.username,
          },
        }))}
        renderRowAction={(id) => <ChangeLink onClick={() => startEdit(id)} />}
        empty={t("signontab_users_empty")}
        isLoading={isLoading}
        loadError={isError ? t("settings_load_failed") : undefined}
      />
    </SettingsCard>
  );
}

export default SignontabUsersContainer;
